

import { Component, Mixins } from 'vue-property-decorator';
import PopupMixin from '~common-mixins/PopupMixin';
import { DevicesModule } from '~/store/modules/devices';
import { UserModule } from '~/store/modules/user';
import { humanReadableField } from '~/filters/humanReadableField';
import { Device, ImportDataParams, TransferSourceItem, TransformedMigrationData } from '~/interfaces/device.interface';
import { LoadingModule } from '~common-store/modules/loading';
import TopText from './TopText.vue';
import SourceSelector from './SourceSelector.vue';
import apiService from '~/services/api.service';
import { NotificationsModule } from '~common-store/modules/notifications';

@Component({
    components: {
        TopText,
        SourceSelector,
    },
})
export default class OperationImportDataPopup extends Mixins(PopupMixin) {
    showSuccessMessage = false;

    get gatewayMigrationData(): TransformedMigrationData {
        return DevicesModule.gatewayMigrationData;
    }

    set gatewayMigrationData(data: TransformedMigrationData) {
        DevicesModule.setGatewayMigrationData(data);
    }

    get modulesMigrationData(): TransformedMigrationData[] {
        return DevicesModule.modulesMigrationData;
    }

    set modulesMigrationData(data: TransformedMigrationData[]) {
        DevicesModule.setModulesMigrationData(data);
    }

    get currentGateway(): Device | undefined {
        return DevicesModule.currentGateway;
    }

    get title(): string {
        return `import data to ${humanReadableField(this.currentGateway?.type || '')}: ${this.currentGateway?.name} (${this.currentGateway?.id})`;
    }

    get userId(): string {
        return UserModule.id;
    }

    getDeviceById(id: string): Device | undefined {
        return DevicesModule.getDeviceById(id);
    }

    setSelectedGatewaySource($event: TransferSourceItem | null): void {
        this.gatewayMigrationData = { ...this.gatewayMigrationData, selectedSource: $event };
    }

    setSelectedModuleSource($event: TransferSourceItem | null, index: number): void {
        const modulesMigrationCopy = JSON.parse(JSON.stringify(this.modulesMigrationData));
        modulesMigrationCopy[index].selectedSource = $event;
        this.modulesMigrationData = modulesMigrationCopy;
    }

    // import is disabled if there is no gateway selected and there is no selected module transfer
    get isImportDisabled(): boolean {
        return !this.gatewayMigrationData.selectedSource
            && this.modulesMigrationData.every((moduleMigrationData) => !moduleMigrationData.selectedSource);
    }

    buildImportDataParams() {
        const imports: ImportDataParams[] = [];
        const involvedGateways = [];
        const selectedSourceBridgeId = this.gatewayMigrationData?.selectedSource?.bridge_id;
        if (selectedSourceBridgeId) {
            imports.push({
                user_id: this.userId,
                transfert_description: {
                    bridge: selectedSourceBridgeId,
                    target: this.currentGateway?.id
                }
            });
            involvedGateways.push(selectedSourceBridgeId, this.currentGateway?.id);
        }
        this.modulesMigrationData.forEach((migrationData) => {
            const selectedModuleSource = migrationData.selectedSource;
            if (selectedModuleSource) {
                const moduleMigrationData = {
                    target_id: migrationData.target,
                    source_id: selectedModuleSource.module_id as string,
                };
                // find the import data entry that has the same bridge as the current module
                const sameGatewayData = imports.find((importData) => importData.transfert_description.bridge === selectedModuleSource?.bridge_id);
                involvedGateways.push(selectedModuleSource.bridge_id);

                if (sameGatewayData) {
                    if (!sameGatewayData.transfert_description.modules) {
                        sameGatewayData.transfert_description.modules = [];
                    }
                    sameGatewayData.transfert_description.modules.push(moduleMigrationData);
                } else {
                    imports.push({
                        user_id: this.userId,
                        transfert_description: {
                            bridge: selectedModuleSource.bridge_id,
                            modules: [moduleMigrationData]
                        }
                    });
                }
            }
        });
        // we get number of involved gateways by removing duplicates from involvedGateways array
        return { imports, numberOfInvolvedGateways: [...new Set(involvedGateways)].length };
    }

    async importData(): Promise<void> {
        try {
            LoadingModule.showLoading(true);
            const { imports, numberOfInvolvedGateways } = this.buildImportDataParams();
            if (numberOfInvolvedGateways > 2) {
                NotificationsModule.pushNotification({
                    title: 'Error',
                    text: 'There are too many gateways involved. Maximum is 2.',
                    type: 'error',
                    duration: 2000,
                });
            } else {
                for (let index = 0; index < imports.length; index++) {
                    await apiService.adminmigratedata2(imports[index]);
                }
                NotificationsModule.pushNotification({
                    title: 'Success',
                    text: 'The task has been sent to the server, you will receive an e-mail when the migration will be finished. This process can take several minutes.',
                    type: 'success',
                    duration: 10000,
                });
            }
        } catch (error) {
            console.error(error);
        } finally {
            LoadingModule.showLoading(false);
        }
    }
}
